<template>
  <v-app-bar
    app
    dense
    clipped-left
    clipped-right
    :class="appEnvironmentStyle"
    style="z-index: 100;"
  >
    <v-toolbar-title
      class="d-flex align-center"
      style="cursor: pointer"
      @click="$router.push(user.homeUrl)"
    >
      <img
        alt="TPA Solutions"
        src="../../assets/logo.png"
        height="32"
      >

      <div class="ml-3 d-flex align-center" style="height: 32px;">
        <img :src="$store.getters.currentCustomer?.logoBase64" alt="" height="32px" >
      </div>
    </v-toolbar-title>

    <v-spacer />

    <v-toolbar-items class="hidden-sm-and-down align-center">
      <template v-for="(item, i) in mainItems.filter(x => $store.getters.isSuperAdmin || !x.permissions || $store.getters.hasAnyPermission(x.permissions))">
        <v-btn
          v-if="!item.children || item.children.length == 0"
          :key="i"
          :to="item.route"
          text
          small
        >
          <v-icon v-if="item.icon" left>
            {{ item.icon }}
          </v-icon>
          <span>
            {{ item.title }}
          </span>
          <v-icon v-if="item.beta" small>
            mdi-test-tube
          </v-icon>
        </v-btn>

        <v-menu
          v-else
          :key="i + '-menu'"
          :offset-y="true"
          min-width="200"
        >
          <template #activator="{ on, attrs }">
            <v-btn
              text
              small
              v-bind="attrs"
              v-on="on"
              :class="{ 'v-btn--active': $route.path.includes(item.route) }"
            >
              <v-icon v-if="item.icon" :left="$vuetify.breakpoint.lgAndUp" :icon="$vuetify.breakpoint.mdAndDown"> {{ item.icon }} </v-icon>

              <span class="hidden-md-and-down">{{ item.title }}</span>
              <v-icon class="hidden-md-and-down" right>mdi-chevron-down</v-icon>
            </v-btn>
          </template>

          <v-list class="pa-0" dense>
            <template v-for="(child, si) in item.children">
              <v-list-item
                :key="si"
                :to="child.route"
                exact
                :disabled="child.disabled"
              >
                <v-list-item-title>{{ child.title }}</v-list-item-title>
                <v-list-item-icon v-if="child.beta">
                  <v-icon small color="grey"> mdi-test-tube </v-icon>
                </v-list-item-icon>
              </v-list-item>

              <v-list-item
                v-for="item in child.subItems"
                :key="item.route"
                :to="item.route"
                dense
                :disabled="item.disabled"
                class="pl-8"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ item.title }}</v-list-item-title>
                  <v-list-item-icon v-if="child.beta">
                    <v-icon small color="grey"> mdi-test-tube </v-icon>
                  </v-list-item-icon>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>
      <v-btn
        text
        @click.native="$store.dispatch('toggleShowOnlyRelevantContent')"
      >
        <v-switch
          v-model="showOnlyRelevant"
          :loading="$store.state.loadingToggleShowOnlyRelevantContent"
          color="teal"
          readonly
          dense
          hide-details
        />
        <v-fade-transition leave-absolute>
          <v-icon
            :key="`show-${showOnlyRelevant}`"
            :color="showOnlyRelevant ? 'teal' : ''"
          >
            {{ showOnlyRelevant ? 'mdi-account' : 'mdi-account-off' }}
          </v-icon>
        </v-fade-transition>
      </v-btn>
  
      <v-menu :offset-y="true">
        <template #activator="{ on, attrs }">
          <v-btn
            class="hidden-sm-and-down"
            text
            small
            v-bind="attrs"
            v-on="on"
          >
            <div class="d-flex flex-column">
              <div>
                {{ user.fullName }}
              </div>
              <small v-if="$store.getters.userHasMultipleCustomers" class="mt-1" style="font-size: x-small;">
                {{ $store.getters.currentCustomer?.title }}
              </small>
            </div>
          </v-btn>
        </template>
  
        <v-sheet>
          <v-list class="pa-0" dense>
            <v-list-item
              v-for="item in profileItems"
              :key="item.route"
              :to="item.route"
            >
              <v-list-item-title>{{ item.title }}</v-list-item-title>
              <v-list-item-icon>
                <v-icon> {{ item.icon }} </v-icon>
              </v-list-item-icon>
            </v-list-item>
          </v-list>
  
          <div class="d-flex justify-end">
            <v-btn-toggle
              dense
              multiple
              class="ma-3"
            >
              <v-btn
                icon
                @click.stop="toggleTheme"
              >
                <v-icon>
                  {{ !$vuetify.theme.dark ? 'mdi-weather-sunny' : 'mdi-weather-sunny-off' }}
                </v-icon>
              </v-btn>
              <v-btn
                v-if="$store.getters.userHasMultipleCustomers"
                outlined
                @click="$store.dispatch('setPrimaryCustomerId', null)"
              >
                <v-icon class="mr-2"> mdi-swap-horizontal </v-icon>
                <v-icon> mdi-domain </v-icon>
              </v-btn>
            </v-btn-toggle>
          </div>
        </v-sheet>
      </v-menu>
    </v-toolbar-items>

    <v-toolbar-items class="hidden-md-and-up align-center">
      <v-menu
        v-model="showMobileMenu"
        :offset-y="true"
        :close-on-content-click="false"
      >
        <template #activator="{ on, attrs }">
          <v-btn
            class="hidden-md-and-up"
            icon
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
  
        <v-list dense subheader>
          <v-subheader>Navigation</v-subheader>
          <template v-for="navItem in mainItems.filter(x => $store.getters.isSuperAdmin || !x.permissions || $store.getters.hasAnyPermission(x.permissions))">
            <v-list-item
              v-if="!navItem.children?.length"
              :key="navItem.route"
              :to="navItem.route"
            >
              <v-list-item-icon>
                <v-icon>
                  {{ navItem.icon }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  {{ navItem.title }}
                </v-list-item-title>              
              </v-list-item-content>
            </v-list-item>
            <v-list-group
              v-else
              :key="navItem.title"
              :prepend-icon="navItem.icon"
            >
              <template #activator>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ navItem.title }}
                  </v-list-item-title>
                </v-list-item-content>
              </template>

              <v-list-item
                v-for="child in navItem.children"
                :key="child.route"
                :to="child.route"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    {{ child.title }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-group>
          </template>
          <v-subheader>{{ user.fullName }}</v-subheader>            
          <v-list-item
            v-for="navItem in profileItems"
            :key="navItem.route"
            :to="navItem.route"
          >
            <v-list-item-icon>
              <v-icon> {{ navItem.icon }} </v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ navItem.title }}</v-list-item-title>              
          </v-list-item>
        </v-list>
      </v-menu>
    </v-toolbar-items>
  </v-app-bar>
</template>

<script>
import config from '@/assets/config.json'

export default {
  name: 'Header',
  data() {
    return {
      showMobileMenu: false
    }
  },
  computed: {
    mainItems() {
      return [
        {
          title: this.$t('header.support'),
          route: '/supportticket',
          icon: 'mdi-headset',
          permissions: ['Support']
        },
        {
          title: this.$t('header.taskrequest'),
          route: '/taskrequest',
          icon: 'mdi-format-list-checks',
          permissions: ['Delivery']
        },
        {
          title: this.$t('header.delivery'),
          route: '/delivery',
          icon: 'mdi-hammer-screwdriver',
          permissions: ['Delivery']
        },
        {
          title: this.$t('header.locations'),
          route: '/locations',
          icon: 'mdi-earth',
          permissions: ['Location']
        },
        {
          title: this.$t('header.info.title'),
          icon: 'mdi-information-outline',
          children: [
            {
              title: this.$t('header.info.organization'),
              route: '/organization',
              permissions: ['Organization']
            },
            {
              title: this.$t('header.info.parts'),
              route: '/part',
              permissions: ['Part']
            },
            {
              title: this.$t('header.info.guidelines'),
              route: '/viden/retningslinjer',
              permissions: ['DocumentsRetningslinjer']
            },
            {
              title: this.$t('header.info.instructions'),
              route: '/viden/vejledninger',
              permissions: ['DocumentsVejledninger']
            },
          ]
        },
        {
          title: this.$t('header.parts.title'),
          icon: 'mdi-warehouse',
          disabled: true,
          permissions: ['AdministrationPart'],
          children: [
            {
              title: this.$t('header.parts.list'),
              route: '/parts'
            },
            {
              title: this.$t('header.parts.priceReport'),
              route: '/parts/price-report'
            },
            {
              title: this.$t('header.parts.pricePending'),
              route: '/parts/price-pending'
            },
            {
              title: this.$t('header.parts.tpaCategories'),
              route: '/parts/tpa-categories'
            },
            {
              title: this.$t('header.parts.customerCategories'),
              route: '/parts/customer-categories'
            },
            {
              title: this.$t('header.parts.supplierOrders'),
              route: '/parts/supplier-orders',
              beta: true
            },
            {
              title: this.$t('header.parts.manufacturers'),
              route: '/parts/manufacturers'
            },
            {
              title: this.$t('header.parts.suppliers'),
              route: '/parts/suppliers'
            },
            {
              title: this.$t('header.parts.updatePrices'),
              route: '/parts/update-prices',
              beta: true
            },
          ]
        },
        {
          title: this.$t('header.admin.title'),
          icon: 'mdi-cog-outline',
          permissions: [
            'AdministrationUser',
            'AdministrationRole',
            'AdministrationDocumentsContracts',
            'AdministrationPart',
            'AdministrationCustomer',
            'AdministrationOrganization',
            'AdministrationUser'
          ],
          children: [
            {
              title: this.$t('header.admin.users'),
              route: '/admin/personrole',
              permissions: ['AdministrationUser']
            },
            {
              title: this.$t('header.admin.accessRequests'),
              route: '/admin/access-requests',
              permissions: ['AdministrationUser'],
              disabled: !this.$store.getters.isSuperAdmin
            },
            {
              title: this.$t('header.admin.responsibilities'),
              route: '/admin/responsibilityrole',
              permissions: ['AdministrationRole']
            },
            {
              title: this.$t('header.admin.roles'),
              route: '/admin/role',
              permissions: ['AdministrationRole'],
              disabled: true
            },
            {
              title: this.$t('header.admin.contracts'),
              route: '/docs/contract',
              permissions: ['AdministrationDocumentsContracts'],
              disabled: true
            },
            {
              title: this.$t('header.admin.invoiceDraft'),
              route: '/admin/invoice-draft',
              permissions: ['AdministrationPart']
            },
            {
              title: this.$t('header.admin.customers'),
              route: '/admin/customers',
              permissions: ['AdministrationCustomer']
            },
            {
              title: this.$t('header.admin.organizations'),
              route: '/admin/organizations',
              permissions: ['AdministrationOrganization']
            },
            {
              title: this.$t('header.admin.salaryBasis'),
              route: '/admin/salary-basis',
              permissions: ['AdministrationUser']
            },
            {
              title: this.$t('header.admin.systemLog'),
              route: '/admin/system-log',
              permissions: ['AdministrationUser']
            },
          ]
        }
      ]
    },
    profileItems() {
      return [
        {
          title: this.$t('header.account'),
          route: '/account',
          icon: 'mdi-card-account-details-outline'
        },
        {
          title: this.$t('header.help'),
          route: '/help',
          icon: 'mdi-head-question-outline'
        },
        {
          title: this.$t('header.terms'),
          route: '/terms',
          icon: 'mdi-file-check-outline'
        },
        {
          title: this.$t('header.about'),
          route: '/about',
          icon: 'mdi-information-outline'
        },
        {
          title: this.$t('header.logoff'),
          route: '/logoff',
          icon: 'mdi-logout-variant'
        }
      ]
    },
    user() {
      return this.$store.getters.currentUser;
    },
    showOnlyRelevant: {
      get() {
        return this.$store.getters.showOnlyRelevantContent;
      },
      set(value) {
        this.$store.dispatch('toggleShowOnlyRelevantContent')
      }
    },
    appEnvironment() {
      return config.environment;
    },    
    appEnvironmentStyle() {
      var strValue = config.environment;
      if (strValue) {
        strValue=strValue.toLowerCase()+"EnvStyle"
      }
      return strValue;
    },
  },
  methods: {
    toggleTheme() {
      this.$vuetify.theme.dark = !this.$vuetify.theme.dark;

      localStorage.setItem('prefers-dark', this.$vuetify.theme.dark);
    },
    getLogoFilename(customerName) {
      switch (customerName) {
        case 'Udenrigsministeriet':
          return 'um-logo-black.png'
        case 'Syddansk Universitet':
          return ''
        default:
          return ''
      }
    }
  },
  watch: {
    '$route' (to, from) {
      this.showMobileMenu = false;
    }
  }
}
</script>
